import React, { useMemo, useRef, useState } from "react";

import { EquipoMensaje } from "../../../services/v2/prospects.service";

interface Pendiente { clave: number; canalId: number; body: string; at: string; desde: number; id: number | null; cita: EquipoMensaje | null; }

let ultimaClave = 0;

/** Mensaje en camino (todavía sin ✓), como el relojito de WhatsApp. */
export const Reloj: React.FC = () => (
	<span className="cv-tick eqm-tick enviando" title="Enviando" aria-label="Enviando">
		<svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true"><circle cx="8" cy="8" r="6.3" fill="none" stroke="currentColor" strokeWidth="1.4" /><path d="M8 4.6V8l2.3 1.4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
	</span>
);

/**
 * Texto de WappTeam al instante, como WhatsApp (Esteban, 2026-10-20: "se tarda en enviar a pesar de que ya le di click"):
 * el cuadro se vacía al tocar enviar y el mensaje sale abajo con relojito; cuando el servidor lo guarda (o llega por push)
 * el de verdad ocupa su lugar con sus palomitas. Los textos salen en fila, en el orden en que se escribieron.
 * Lo usan la pantalla completa (equipoView) y las ventanitas (wappteamDock).
 */
export const useEnCamino = (canalId: number | null, mensajes: EquipoMensaje[], yoId: number | null) => {
	const [pend, setPend] = useState<Pendiente[]>([]);
	const fila = useRef<Promise<void>>(Promise.resolve());
	const quitar = (clave: number) => setPend((p) => p.filter((x) => x.clave !== clave));

	/**
	 * enviar() devuelve el id del mensaje guardado: en ese instante la burbuja cambia el relojito por su palomita (ya con el
	 * id real) sin esperar a que se recargue la conversación. ok/mal corren al terminar (mal: ya se quitó la burbuja).
	 */
	const mandar = (canal: number, body: string, cita: EquipoMensaje | null, enviar: () => Promise<number | null>, luego: { ok: () => void; mal: (e: unknown) => void }) => {
		const clave = ++ultimaClave;
		const desde = mensajes.reduce((n, m) => Math.max(n, m.id), 0);
		setPend((p) => [...p, { clave, canalId: canal, body, at: new Date().toISOString(), desde, id: null, cita }]);
		fila.current = fila.current.then(() => enviar().then((id) => {
			if (id) {
				setPend((p) => p.map((x) => (x.clave === clave ? { ...x, id } : x)));
				setTimeout(() => quitar(clave), 20000);   // por si la recarga no lo trae: el respaldo lo pinta
			} else quitar(clave);
			luego.ok();
		}, (e) => { quitar(clave); luego.mal(e); }));
	};

	// Cada mensaje de verdad toma el lugar de uno solo en camino: por id si ya se sabe; si no, por el texto (llegó por push
	// antes que la respuesta del envío). Mientras llega, el que ya tiene id se pinta como enviado (✓, con su id de verdad);
	// el que todavía no, con relojito.
	const vista = useMemo((): EquipoMensaje[] => {
		const aqui = pend.filter((x) => x.canalId === canalId);
		if (!aqui.length) return mensajes;
		const usados = new Set<number>();
		const faltan = aqui.filter((x) => {
			const real = mensajes.find((m) => !usados.has(m.id) && (x.id !== null ? m.id === x.id
				: m.id > x.desde && m.user_id === yoId && (m.body_crudo || m.body || "").trim() === x.body));
			if (real) usados.add(real.id);
			return !real;
		});
		return faltan.length ? [...mensajes, ...faltan.map((x): EquipoMensaje => ({
			id: x.id ?? -x.clave, user_id: yoId || 0, author_name: "", body: x.body, prospect_id: null, created_at: x.at, edited_at: null, deleted_at: null,
			reply_to_id: x.cita?.id ?? null, r_author: x.cita?.author_name ?? null, r_body: x.cita?.body ?? null, r_file_tipo: x.cita?.file_tipo ?? null,
			file_tipo: null, file_mime: null, file_name: null, file_size: null, duracion: null, menciones: [], reacciones: [],
			p_first_name: null, p_last_name: null, p_phone: null, p_etapa: null, p_status: null, p_curso: null, enCamino: x.id === null,
		}))] : mensajes;
	}, [pend, mensajes, canalId, yoId]);

	return { mandar, vista };
};
